import React from 'react';
import { X, AlertTriangle, Clock, Calendar } from 'lucide-react';

const PendingEventsWarningModal = ({ 
  isOpen = false, 
  onClose, 
  manifest = "",
  pendingEvents = [],
  pendingEventsCount = 0
}) => {
  if (!isOpen) return null;

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-yellow-50">
          <div className="flex items-center">
            <div className="mr-3 flex-shrink-0 text-yellow-600">
              <AlertTriangle className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Eventos Pendientes de Aprobación
              </h3>
              <p className="text-sm text-gray-600">
                Manifiesto: <span className="font-medium">{manifest}</span>
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-96">
          {/* Warning Message */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div className="flex items-start">
              <AlertTriangle className="w-5 h-5 text-yellow-600 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="text-sm font-medium text-yellow-800 mb-2">
                  No se pueden registrar nuevos eventos
                </h4>
                <p className="text-sm text-yellow-700">
                  Este manifiesto tiene <strong>{pendingEventsCount} evento{pendingEventsCount !== 1 ? 's' : ''} pendiente{pendingEventsCount !== 1 ? 's' : ''}</strong> de aprobación. 
                  Debe aprobar o rechazar los eventos existentes antes de poder:
                </p>
                <ul className="text-sm text-yellow-700 mt-2 ml-4 space-y-1">
                  <li>• Agregar nuevos eventos de estado</li>
                  <li>• Finalizar el manifiesto</li>
                  <li>• Marcar como completado</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Pending Events List */}
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-4 flex items-center">
              <Clock className="w-4 h-4 mr-2 text-orange-500" />
              Eventos Pendientes ({pendingEventsCount})
            </h4>
            
            {pendingEvents.length > 0 ? (
              <div className="space-y-3">
                {pendingEvents.map((event, index) => (
                  <div 
                    key={event.id || index} 
                    className="border border-gray-200 rounded-lg p-4 bg-gray-50"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                            Pendiente
                          </span>
                          <div className="flex items-center text-xs text-gray-500">
                            <Calendar className="w-3 h-3 mr-1" />
                            {formatDate(event.status_date)}
                          </div>
                        </div>
                        <p className="text-sm text-gray-900 font-medium mb-2">
                          {event.status_detail}
                        </p>
                        <p className="text-xs text-gray-500">
                          Creado: {formatDate(event.created_at)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Clock className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                <p className="text-sm">No se encontraron eventos pendientes</p>
              </div>
            )}
          </div>

          {/* Instructions */}
          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="text-sm font-medium text-blue-900 mb-2">
              ¿Cómo proceder?
            </h4>
            <div className="text-sm text-blue-800 space-y-1">
              <p>1. Vaya a la sección de <strong>"Eventos por Aprobar"</strong> en el dashboard principal</p>
              <p>2. Revise y apruebe o rechace cada evento pendiente</p>
              <p>3. Una vez que no haya eventos pendientes, podrá continuar con las operaciones normales</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
          >
            Entendido
          </button>
        </div>
      </div>
    </div>
  );
};

export default PendingEventsWarningModal;